/**
 * @file Routing.
 *
 * ## The shape
 *
 * ```
 * /                                                → onboarding: create a portfolio, or go to yours
 * /workspaces/$workspaceId                         → Dashboard (the app's home)
 * /workspaces/$workspaceId/properties              → Properties
 * /workspaces/$workspaceId/properties/$propertyId  → Property detail
 * /workspaces/$workspaceId/units                   → Units & beds
 * /workspaces/$workspaceId/units/$unitId           → Unit detail
 * /workspaces/$workspaceId/leases                  → Leases
 * /workspaces/$workspaceId/leases/$leaseId         → Lease detail
 * /workspaces/$workspaceId/tenants                 → Tenants
 * /workspaces/$workspaceId/tenants/$tenantId       → Tenant detail
 * /workspaces/$workspaceId/guarantors              → Guarantors
 * /workspaces/$workspaceId/guarantors/$guarantorId → Guarantor detail
 * /workspaces/$workspaceId/ledger                  → Ledger
 * /workspaces/$workspaceId/rent-charges            → Rent & charges
 * /workspaces/$workspaceId/payments                → Payments
 * /workspaces/$workspaceId/expenses                → Expenses
 * /workspaces/$workspaceId/maintenance             → Maintenance
 * /workspaces/$workspaceId/maintenance/$requestId  → Maintenance detail
 * /workspaces/$workspaceId/turnover                → Turnover
 * /workspaces/$workspaceId/reports                 → Reports
 * /workspaces/$workspaceId/account                 → Account & team
 * /accept-invite?w=…&token=…                       → join a workspace from an emailed invitation
 * /workspaces/$workspaceId/activity                → Activity
 * ```
 *
 * `$workspaceId` is in the path because it is the RLS tenant key — a URL has to
 * say which tenant's data it addresses. **It is plumbing, not a screen.** The user
 * never picks one: `/` sends them to their portfolio's dashboard, and a landlord
 * with more than one portfolio switches from the sidebar. The old flow made a
 * mandatory picker the first thing you saw, which was the tenancy key leaking into
 * the UI.
 *
 * ## Code-based, not file-based
 *
 * TanStack Router's headline feature is a generated route tree plus a Vite plugin.
 * This app has four routes and a convention of making decisions legible in the
 * source, so they are declared here. `createAppRouter` takes a history, which is
 * what lets tests mount any URL directly.
 *
 * ## The workspace id is validated, not trusted
 *
 * It can be typed, bookmarked or stale, so the layout route re-checks it against
 * the caller's own memberships from `GET /me` and renders "not available" when it
 * does not match — matching the API, where a non-member gets 404 rather than 403
 * (apps/api/src/authz.ts).
 */
import { lazy, Suspense, useEffect, type ComponentType } from "react";
import {
  createRootRoute,
  createRoute,
  createRouter,
  useNavigate,
  useParams,
  useSearch,
  type RouterHistory,
} from "@tanstack/react-router";
import { isLandlordRole } from "@neighboros/shared";
import { AppShell, RESET_PASSWORD_PATH } from "./AppShell";
import { useAuthValue } from "./auth/context";
import { useMe } from "./api/queries";
import type { ApiWorkspace } from "./api/types";
import { DashboardScreen } from "./components/DashboardScreen";
import { ResetPasswordScreen } from "./components/ResetPasswordScreen";
import { WorkspaceGate } from "./components/WorkspaceGate";
import { WorkspaceLayout } from "./components/WorkspaceLayout";
import { ErrorCard } from "./components/ErrorCard";
import { NotFound } from "./components/NotFound";
import { RoutePending } from "./components/RoutePending";
import { Button, Card, CenteredPanel, ErrorPanel, Spinner } from "./components/ui";

/**
 * Screens other than the landing page are split into their own chunks and fetched on first visit.
 * Kept in the main chunk on purpose: AppShell and sign-in (needed to paint anything), the
 * onboarding gate, the workspace layout (sidebar) and the Dashboard (the landing route), so the
 * common first view needs one round trip. Everything else loads behind a delayed spinner.
 */
function lazyScreen<Module, Name extends keyof Module>(
  load: () => Promise<Module>,
  name: Name,
): Module[Name] {
  const Lazy = lazy(async () => ({ default: (await load())[name] as ComponentType<object> }));
  function LazyScreen(props: object) {
    return (
      <Suspense fallback={<RoutePending />}>
        <Lazy {...props} />
      </Suspense>
    );
  }
  return LazyScreen as Module[Name];
}

const AcceptInviteScreen = lazyScreen(() => import("./components/AcceptInviteScreen"), "AcceptInviteScreen");
const ActivityScreen = lazyScreen(() => import("./components/ActivityScreen"), "ActivityScreen");
const AccountScreen = lazyScreen(() => import("./components/AccountScreen"), "AccountScreen");
const ExpensesScreen = lazyScreen(() => import("./components/ExpensesScreen"), "ExpensesScreen");
const GuarantorDetail = lazyScreen(() => import("./components/GuarantorDetail"), "GuarantorDetail");
const GuarantorsScreen = lazyScreen(() => import("./components/GuarantorsScreen"), "GuarantorsScreen");
const LedgerScreen = lazyScreen(() => import("./components/LedgerScreen"), "LedgerScreen");
const LeaseDetail = lazyScreen(() => import("./components/LeaseDetail"), "LeaseDetail");
const LeasesScreen = lazyScreen(() => import("./components/LeasesScreen"), "LeasesScreen");
const MaintenanceDetail = lazyScreen(() => import("./components/MaintenanceDetail"), "MaintenanceDetail");
const MaintenanceScreen = lazyScreen(() => import("./components/MaintenanceScreen"), "MaintenanceScreen");
const PaymentsScreen = lazyScreen(() => import("./components/PaymentsScreen"), "PaymentsScreen");
const PropertiesScreen = lazyScreen(() => import("./components/PropertiesScreen"), "PropertiesScreen");
const PropertyDetail = lazyScreen(() => import("./components/PropertyDetail"), "PropertyDetail");
const RentChargesScreen = lazyScreen(() => import("./components/RentChargesScreen"), "RentChargesScreen");
const ReportsScreen = lazyScreen(() => import("./components/ReportsScreen"), "ReportsScreen");
const TenantDetail = lazyScreen(() => import("./components/TenantDetail"), "TenantDetail");
const TenantsScreen = lazyScreen(() => import("./components/TenantsScreen"), "TenantsScreen");
const TurnoverScreen = lazyScreen(() => import("./components/TurnoverScreen"), "TurnoverScreen");
const UnitDetail = lazyScreen(() => import("./components/UnitDetail"), "UnitDetail");
const UnitsScreen = lazyScreen(() => import("./components/UnitsScreen"), "UnitsScreen");

const rootRoute = createRootRoute({ component: AppShell, notFoundComponent: NotFound });

/** `/` — onboarding only. Sends you to your portfolio; there is no picker. */
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: WorkspaceGate,
});

/**
 * `/reset-password` — the ONLY route reachable while signed out (AppShell lets it through). The API
 * redirects the emailed link here with `?token=...`, or `?error=INVALID_TOKEN` for a bad one.
 */
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: RESET_PASSWORD_PATH,
  validateSearch: (search: Record<string, unknown>): { token?: string; error?: string } => ({
    ...(typeof search.token === "string" && search.token.length > 0 ? { token: search.token } : {}),
    ...(typeof search.error === "string" && search.error.length > 0 ? { error: search.error } : {}),
  }),
  component: ResetPasswordRoute,
});

function ResetPasswordRoute() {
  const { token, error } = resetPasswordRoute.useSearch();
  const { actions } = useAuthValue();
  const navigate = useNavigate();
  return (
    <ResetPasswordScreen
      token={token}
      error={error}
      onReset={actions.resetPassword}
      onRequestPasswordReset={actions.requestPasswordReset}
      onDone={() => void navigate({ to: "/" })}
    />
  );
}

/**
 * `/accept-invite?w=<workspaceId>&token=<token>` — the page an emailed team invitation lands on.
 * Signed out, AppShell shows the sign-in form at this same URL; once signed in this renders and calls
 * the accept endpoint. It is not under a workspace: the person is not a member yet.
 */
const acceptInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/accept-invite",
  validateSearch: (search: Record<string, unknown>): { w?: string; token?: string } => ({
    ...(typeof search.w === "string" && search.w.length > 0 ? { w: search.w } : {}),
    ...(typeof search.token === "string" && search.token.length > 0 ? { token: search.token } : {}),
  }),
  component: AcceptInviteRoute,
});

function AcceptInviteRoute() {
  const { w, token } = acceptInviteRoute.useSearch();
  return <AcceptInviteScreen workspaceId={w} token={token} />;
}

/** The workspace frame. Everything below it gets the sidebar. */
const workspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/workspaces/$workspaceId",
  component: WorkspaceFrame,
});

const dashboardRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/",
  component: DashboardRoute,
});

const propertiesRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/properties",
  component: PropertiesRoute,
});

const propertyRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/properties/$propertyId",
  component: PropertyRoute,
});

const unitsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/units",
  component: UnitsRoute,
});

/**
 * `/units/$unitId` — one unit, FLAT rather than nested under its property.
 *
 * The nesting is the tempting shape and it is the wrong one: the URL would force
 * every caller to carry a `propertyId` it does not have. A lease row knows its unit
 * and not the building's id, a rent charge knows its lease, a turnover row knows only
 * the unit, and a payment knows its charge — so a nested URL would mean fetching a
 * parent just to build a link. One canonical URL per thing, reachable from anywhere
 * that names the thing.
 */
const unitRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/units/$unitId",
  component: UnitRoute,
});

const leasesRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/leases",
  component: LeasesRoute,
  /**
   * `?unitId=…` is how a deep link says WHICH unit to start a lease on: a URL that
   * arrives with the unit already chosen, so the form opens on it rather than asking
   * the person to click a second button to reach the choice they just made.
   *
   * **Phase 2 moved the PRIMARY route to creating a lease onto the unit's own page**
   * (`/units/$unitId` → "Start a lease"), because that is the page for the thing being
   * leased. This parameter stays, and it is not dead plumbing: it is the linkable
   * form of "start a lease on THIS unit" — a bookmark, an email, a link from anywhere
   * that knows a unit id — and the API's own 404/400 behaviour makes a stale one
   * harmless (the picker falls back to no selection).
   *
   * Validated rather than trusted — a URL can be typed, bookmarked or stale, and a
   * non-string `unitId` would reach the picker as a garbage value. An absent or
   * malformed parameter is simply "no preselection", which is the form's own
   * default; a blank string is normalised to `undefined` so "no choice" has one
   * representation instead of two.
   */
  validateSearch: (search: Record<string, unknown>): { unitId?: string } => {
    const unitId = search.unitId;
    return typeof unitId === "string" && unitId.length > 0 ? { unitId } : {};
  },
});

/**
 * `/leases/$leaseId` — the hub, and FLAT for the same reason `/units/$unitId` is: a
 * rent charge, a payment and a ledger entry all name a lease and none of them carries
 * a `propertyId`, a `unitId` or anything else a nested URL would demand.
 */
const leaseRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/leases/$leaseId",
  component: LeaseRoute,
});

const tenantsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/tenants",
  component: TenantsRoute,
});

/**
 * `/tenants/$leaseTenantId` — one person on a lease, FLAT for the same reason
 * `/units/$unitId` and `/leases/$leaseId` are.
 *
 * The parameter is named for the TABLE (`lease_tenants.id`) because that is the id the
 * API takes and the one every row already carries; the URL segment is the product word.
 * A nested URL would force a caller to know the lease first, which is exactly the hop
 * this page removes.
 */
const tenantRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/tenants/$tenantId",
  component: TenantRoute,
});

const guarantorsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/guarantors",
  component: GuarantorsRoute,
});

/**
 * `/guarantors/$guarantorId` — one third party and their consent record.
 *
 * Flat, and NOT nested under the tenant or the lease they stand behind: a guarantor can
 * be recorded before the tenancy exists (`lease_tenant_id` is nullable), so a nested URL
 * could not address the very rows this page most needs to reach.
 */
const guarantorRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/guarantors/$guarantorId",
  component: GuarantorRoute,
});

const ledgerRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/ledger",
  component: LedgerRoute,
});

const rentChargesRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/rent-charges",
  component: RentChargesRoute,
});

const paymentsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/payments",
  component: PaymentsRoute,
});

const maintenanceRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/maintenance",
  component: MaintenanceRoute,
});

/**
 * `/maintenance/$requestId` — one request and its timeline.
 *
 * Flat, like every other detail route: a unit page, a lease page and the work list all
 * name a request id and none of them carries a `unitId` to nest with.
 */
const maintenanceRequestRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/maintenance/$requestId",
  component: MaintenanceRequestRoute,
});

const expensesRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/expenses",
  component: ExpensesRoute,
});

const turnoverRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/turnover",
  component: TurnoverRoute,
});

const reportsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/reports",
  component: ReportsRoute,
});

const accountRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/account",
  component: AccountRoute,
});

const activityRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/activity",
  component: ActivityRoute,
});

const routeTree = rootRoute.addChildren([
  resetPasswordRoute,
  acceptInviteRoute,
  indexRoute,
  workspaceRoute.addChildren([
    dashboardRoute,
    propertiesRoute,
    propertyRoute,
    unitsRoute,
    unitRoute,
    leasesRoute,
    leaseRoute,
    tenantsRoute,
    tenantRoute,
    guarantorsRoute,
    guarantorRoute,
    ledgerRoute,
    rentChargesRoute,
    paymentsRoute,
    expensesRoute,
    maintenanceRoute,
    maintenanceRequestRoute,
    turnoverRoute,
    reportsRoute,
    accountRoute,
    activityRoute,
  ]),
]);

/**
 * Build a router. `history` is injectable so tests can mount a specific URL with
 * a memory history; production gets the browser's.
 */
export function createAppRouter(options: { readonly history?: RouterHistory } = {}) {
  return createRouter({
    routeTree,
    ...(options.history === undefined ? {} : { history: options.history }),
    defaultPreload: false,
    // No route has a loader, so "pending" only ever means a lazy chunk is downloading. Wait a beat
    // before showing it so a fast (cached) load does not flash a spinner.
    defaultPendingComponent: RoutePending,
    defaultPendingMs: 150,
    defaultNotFoundComponent: NotFound,
    defaultErrorComponent: RouteError,
  });
}

/** A render error inside a route: calm card, nothing technical on screen, reported once. */
function RouteError({ error }: { readonly error: unknown }) {
  return <ErrorCard error={error} />;
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}

type Resolved =
  | { readonly kind: "loading" }
  | { readonly kind: "error"; readonly error: unknown; readonly retry: () => void }
  | { readonly kind: "missing" }
  /** A membership, but a RESIDENT one: the landlord shell must not open on it. */
  | { readonly kind: "resident" }
  | { readonly kind: "found"; readonly workspace: ApiWorkspace };

/**
 * Resolve the workspace named in the URL against the caller's own memberships.
 *
 * `GET /me` is the authorization map the SPA already depends on, so a workspace
 * that is not in it is mistyped, stale, or someone else's — and all three produce
 * the same screen, disclosing nothing about which ids exist.
 *
 * A workspace that IS in it but under a resident role (`tenant`, `guarantor`) is a
 * fourth case: the person is a member, so there is nothing to hide, but every
 * landlord route would refuse them (`LANDLORD_ROLES`, shared with the API). The
 * frame bounces that to `/`, where `WorkspaceGate` applies the same rule and lands
 * on a landlord workspace if they have one, or on the calm resident screen.
 */
function useResolvedWorkspace(): Resolved {
  const { workspaceId } = useParams({ from: "/workspaces/$workspaceId" });
  const me = useMe();

  if (me.status === "pending") return { kind: "loading" };
  if (me.status === "error") {
    return {
      kind: "error",
      error: me.error,
      retry: () => {
        void me.refetch();
      },
    };
  }

  const workspace = me.data.workspaces.find((candidate) => candidate.id === workspaceId);
  if (workspace === undefined) return { kind: "missing" };
  if (!isLandlordRole(workspace.role)) return { kind: "resident" };
  return { kind: "found", workspace };
}

/** Rendered when the URL names a workspace the caller cannot open. */
function WorkspaceNotAvailable() {
  const navigate = useNavigate();
  return (
    <Card className="items-start gap-3 px-6 py-8">
      <h1 className="text-2xl font-semibold tracking-tight">
        That portfolio isn&rsquo;t available
      </h1>
      <p className="text-sm text-muted-foreground">
        The link may be out of date, or it may not be yours. Your own portfolios are
        one click away.
      </p>
      <Button
        onClick={() => {
          void navigate({ to: "/" });
        }}
      >
        Go to my dashboard
      </Button>
    </Card>
  );
}

/**
 * The frame.
 *
 * Every child route calls `useResolvedWorkspace` too. That is a second call to the
 * same cached `GET /me` — no extra request — and it avoids threading the resolved
 * workspace through a React context purely to save one line per route.
 */
function WorkspaceFrame() {
  const resolved = useResolvedWorkspace();
  const navigate = useNavigate();

  // A resident's direct link is sent to `/` rather than answered here: the gate owns the
  // "where may this person go" decision, and answering it in two places is how the two
  // start disagreeing. `replace`, so Back does not return to the bounce.
  const isResident = resolved.kind === "resident";
  useEffect(() => {
    if (!isResident) return;
    void navigate({ to: "/", replace: true });
  }, [isResident, navigate]);

  if (resolved.kind === "loading" || resolved.kind === "resident") {
    return (
      <CenteredPanel>
        <Spinner />
        Loading…
      </CenteredPanel>
    );
  }
  if (resolved.kind === "error") {
    return <ErrorPanel error={resolved.error} onRetry={resolved.retry} />;
  }
  if (resolved.kind === "missing") return <WorkspaceNotAvailable />;

  return <WorkspaceLayout workspace={resolved.workspace} />;
}

function DashboardRoute() {
  const resolved = useResolvedWorkspace();
  // The frame above already handled every non-found branch; this narrows again
  // because each component resolves independently.
  if (resolved.kind !== "found") return null;
  return <DashboardScreen workspace={resolved.workspace} />;
}

function PropertiesRoute() {
  const resolved = useResolvedWorkspace();
  const navigate = useNavigate();
  if (resolved.kind !== "found") return null;

  const { workspace } = resolved;
  return (
    <PropertiesScreen
      workspace={workspace}
      onOpenProperty={(propertyId) => {
        void navigate({
          to: "/workspaces/$workspaceId/properties/$propertyId",
          params: { workspaceId: workspace.id, propertyId },
        });
      }}
    />
  );
}

function PropertyRoute() {
  const resolved = useResolvedWorkspace();
  const navigate = useNavigate();
  const { propertyId } = useParams({
    from: "/workspaces/$workspaceId/properties/$propertyId",
  });
  if (resolved.kind !== "found") return null;

  const { workspace } = resolved;
  return (
    <PropertyDetail
      workspace={workspace}
      propertyId={propertyId}
      onBack={() => {
        void navigate({
          to: "/workspaces/$workspaceId/properties",
          params: { workspaceId: workspace.id },
        });
      }}
    />
  );
}

function UnitsRoute() {
  const resolved = useResolvedWorkspace();
  if (resolved.kind !== "found") return null;
  return <UnitsScreen workspace={resolved.workspace} />;
}

/**
 * `/units/$unitId` — the unit's own page, and the fix for the reported bug.
 *
 * Starting a lease here navigates to the agreement that was just created rather than
 * back to a list: the person started a lease on THIS unit, so the thing they now want
 * is that lease.
 */
function UnitRoute() {
  const resolved = useResolvedWorkspace();
  const navigate = useNavigate();
  const { unitId } = useParams({ from: "/workspaces/$workspaceId/units/$unitId" });
  if (resolved.kind !== "found") return null;

  const { workspace } = resolved;
  return (
    <UnitDetail
      workspace={workspace}
      unitId={unitId}
      onLeaseCreated={(leaseId) => {
        void navigate({
          to: "/workspaces/$workspaceId/leases/$leaseId",
          params: { workspaceId: workspace.id, leaseId },
        });
      }}
    />
  );
}

function LeasesRoute() {
  const resolved = useResolvedWorkspace();
  const navigate = useNavigate();
  const { unitId } = useSearch({ from: "/workspaces/$workspaceId/leases" });
  if (resolved.kind !== "found") return null;

  const { workspace } = resolved;
  return (
    <LeasesScreen
      workspace={workspace}
      initialUnitId={unitId}
      onLeaseCreated={(leaseId) => {
        // The lease's own page, which is where a tenancy is read and moved. Phase 2
        // built it, so the destination is no longer "the list it just joined".
        void navigate({
          to: "/workspaces/$workspaceId/leases/$leaseId",
          params: { workspaceId: workspace.id, leaseId },
        });
      }}
    />
  );
}

/** `/leases/$leaseId` — one agreement and everything hanging off it. */
function LeaseRoute() {
  const resolved = useResolvedWorkspace();
  const { leaseId } = useParams({ from: "/workspaces/$workspaceId/leases/$leaseId" });
  if (resolved.kind !== "found") return null;
  return <LeaseDetail workspace={resolved.workspace} leaseId={leaseId} />;
}

function TenantsRoute() {
  const resolved = useResolvedWorkspace();
  if (resolved.kind !== "found") return null;
  return <TenantsScreen workspace={resolved.workspace} />;
}

/** `/tenants/$tenantId` — one person, their lease, their unit and their balance. */
function TenantRoute() {
  const resolved = useResolvedWorkspace();
  const { tenantId } = useParams({ from: "/workspaces/$workspaceId/tenants/$tenantId" });
  if (resolved.kind !== "found") return null;
  return <TenantDetail workspace={resolved.workspace} tenantId={tenantId} />;
}

function GuarantorsRoute() {
  const resolved = useResolvedWorkspace();
  if (resolved.kind !== "found") return null;
  return <GuarantorsScreen workspace={resolved.workspace} />;
}

/** `/guarantors/$guarantorId` — one contact and the consent record that gates contact. */
function GuarantorRoute() {
  const resolved = useResolvedWorkspace();
  const { guarantorId } = useParams({
    from: "/workspaces/$workspaceId/guarantors/$guarantorId",
  });
  if (resolved.kind !== "found") return null;
  return <GuarantorDetail workspace={resolved.workspace} guarantorId={guarantorId} />;
}

function LedgerRoute() {
  const resolved = useResolvedWorkspace();
  if (resolved.kind !== "found") return null;
  return <LedgerScreen workspace={resolved.workspace} />;
}

function RentChargesRoute() {
  const resolved = useResolvedWorkspace();
  if (resolved.kind !== "found") return null;
  return <RentChargesScreen workspace={resolved.workspace} />;
}

function PaymentsRoute() {
  const resolved = useResolvedWorkspace();
  if (resolved.kind !== "found") return null;
  return <PaymentsScreen workspace={resolved.workspace} />;
}

function MaintenanceRoute() {
  const resolved = useResolvedWorkspace();
  if (resolved.kind !== "found") return null;
  return <MaintenanceScreen workspace={resolved.workspace} />;
}

/** `/maintenance/$requestId` — one request, its §6.3 buttons and its timeline. */
function MaintenanceRequestRoute() {
  const resolved = useResolvedWorkspace();
  const { requestId } = useParams({
    from: "/workspaces/$workspaceId/maintenance/$requestId",
  });
  if (resolved.kind !== "found") return null;
  return <MaintenanceDetail workspace={resolved.workspace} requestId={requestId} />;
}

function ExpensesRoute() {
  const resolved = useResolvedWorkspace();
  if (resolved.kind !== "found") return null;
  return <ExpensesScreen workspace={resolved.workspace} />;
}

function TurnoverRoute() {
  const resolved = useResolvedWorkspace();
  if (resolved.kind !== "found") return null;
  return <TurnoverScreen workspace={resolved.workspace} />;
}

function ReportsRoute() {
  const resolved = useResolvedWorkspace();
  if (resolved.kind !== "found") return null;
  return <ReportsScreen workspace={resolved.workspace} />;
}

/**
 * Account & team: the roster and the workspace's rent proration setting.
 *
 * The screen receives the resolved workspace, so it knows the caller's ROLE in
 * this workspace (from `GET /me`) — which is what lets it show a member the
 * current proration setting without offering a form the server would refuse.
 */
function AccountRoute() {
  const resolved = useResolvedWorkspace();
  if (resolved.kind !== "found") return null;
  return <AccountScreen workspace={resolved.workspace} />;
}

/** The activity trail: who did what, newest first (landlord roles). */
function ActivityRoute() {
  const resolved = useResolvedWorkspace();
  if (resolved.kind !== "found") return null;
  return <ActivityScreen workspace={resolved.workspace} />;
}

export { routeTree };
